import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  Zap,
  User,
  RefreshCw,
  Link2,
  LayoutDashboard,
  BarChart3,
  Star,
  CheckCircle,
  Briefcase,
  Users,
  Building2,
  Megaphone,
  CreditCard,
  UserPlus,
  Share2,
  TrendingUp,
  ChevronDown
} from 'lucide-react';

const fade = { hidden: { opacity: 0, y: 28 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const coreFeatureIcons = [Zap, User, RefreshCw, Link2, LayoutDashboard, BarChart3];
const coreFeatureColors = [
  'from-[#0A2540] to-[#0A2540]/70',
  'from-violet-600 to-purple-500',
  'from-amber-500 to-orange-400',
  'from-emerald-500 to-teal-400',
  'from-slate-700 to-slate-500',
  'from-rose-500 to-pink-400'
];

const useCaseIcons = [Briefcase, TrendingUp, Building2, Users, Megaphone];

export default function Features() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr');
  };

  useEffect(() => {
    document.title = 'IKATone Features | Digital Business Card for Professionals';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Discover all the powerful features of IKATone. Share your contact, links, and brand instantly with a modern digital solution.');
    return () => {
      document.title = 'IKATone | Smart Digital Business Card for Professionals';
    };
  }, []);

  const coreFeatures = t('featuresPage.core.items', { returnObjects: true });
  const benefits = t('featuresPage.benefits.items', { returnObjects: true });
  const useCases = t('featuresPage.useCases.items', { returnObjects: true });
  const steps = t('featuresPage.howItWorks.steps', { returnObjects: true });
  const reviews = t('featuresPage.socialProof.reviews', { returnObjects: true });

  return (
    <div className="bg-white">

      {/* ── Language Switcher (floating) ── */}
      <button
        onClick={toggleLang}
        className="fixed top-24 right-4 z-50 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 border border-slate-200 shadow-md text-sm font-medium text-slate-700 hover:bg-slate-50 backdrop-blur-sm transition-all"
        data-testid="features-lang-toggle"
      >
        {i18n.language === 'fr' ? 'EN' : 'FR'}
      </button>

      {/* ───── SECTION 1 — HERO ───── */}
      <section className="relative overflow-hidden pt-28 pb-20 sm:pt-36 sm:pb-28">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-[#0A2540]/10/40 rounded-full blur-[100px]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.h1
              variants={fade}
              transition={{ duration: 0.6 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-6"
              data-testid="features-hero-title"
            >
              {t('featuresPage.hero.title')}
            </motion.h1>
            <motion.p
              variants={fade}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="text-lg sm:text-xl text-slate-500 leading-relaxed max-w-2xl mx-auto mb-10"
            >
              {t('featuresPage.hero.subtitle')}
            </motion.p>
            <motion.div variants={fade} transition={{ duration: 0.6, delay: 0.3 }}>
              <Button
                onClick={() => document.getElementById('core-features')?.scrollIntoView({ behavior: 'smooth' })}
                className="h-14 px-8 text-base rounded-lg bg-[#00C48C] text-white shadow-lg shadow-[#00C48C]/25 hover:bg-[#00A67E] hover:shadow-[#00C48C]/40 hover:scale-[1.02] transition-all"
                data-testid="features-hero-cta"
              >
                {t('featuresPage.hero.cta')}
                <ChevronDown className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ───── SECTION 2 — CORE FEATURES GRID ───── */}
      <section className="py-20 bg-slate-50" id="core-features">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            <motion.h2 variants={fade} className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 text-center mb-14">
              {t('featuresPage.core.title')}
            </motion.h2>
            <motion.div variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.isArray(coreFeatures) && coreFeatures.map((feat, i) => {
                const Icon = coreFeatureIcons[i] || Zap;
                const grad = coreFeatureColors[i] || coreFeatureColors[0];
                return (
                  <motion.div
                    key={i}
                    variants={fade}
                    className="bg-white rounded-2xl border border-slate-100 p-7 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
                    data-testid={`core-feature-${i}`}
                  >
                    <div className={`w-13 h-13 rounded-xl bg-gradient-to-br ${grad} flex items-center justify-center mb-5 shadow-md group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{feat.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{feat.desc}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ───── SECTION 3 — BENEFITS ───── */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="text-center">
            <motion.h2 variants={fade} className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-12">
              {t('featuresPage.benefits.title')}
            </motion.h2>
            <motion.div variants={stagger} className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {Array.isArray(benefits) && benefits.map((b, i) => (
                <motion.div
                  key={i}
                  variants={fade}
                  className="flex items-center gap-3 bg-green-50/60 border border-green-100 rounded-xl px-5 py-4 text-left"
                  data-testid={`benefit-${i}`}
                >
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-slate-800 font-medium text-sm">{b}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ───── SECTION 4 — USE CASES ───── */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            <motion.h2 variants={fade} className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 text-center mb-12">
              {t('featuresPage.useCases.title')}
            </motion.h2>
            <motion.div variants={stagger} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
              {Array.isArray(useCases) && useCases.map((uc, i) => {
                const Icon = useCaseIcons[i] || Briefcase;
                return (
                  <motion.div
                    key={i}
                    variants={fade}
                    className="bg-white rounded-2xl p-6 text-center border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all"
                    data-testid={`use-case-${i}`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#0A2540]/5 flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-6 h-6 text-[#0A2540]" />
                    </div>
                    <p className="text-sm font-medium text-slate-700">{uc}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ───── SECTION 5 — HOW IT WORKS ───── */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            <motion.h2 variants={fade} className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 text-center mb-14">
              {t('featuresPage.howItWorks.title')}
            </motion.h2>
            <motion.div variants={stagger} className="grid md:grid-cols-4 gap-8">
              {Array.isArray(steps) && steps.map((step, i) => {
                const icons = [CreditCard, UserPlus, Share2, TrendingUp];
                const StepIcon = icons[i] || CreditCard;
                return (
                  <motion.div key={i} variants={fade} className="relative text-center" data-testid={`step-${i}`}>
                    <div className="w-16 h-16 rounded-2xl bg-[#0A2540] text-white flex items-center justify-center mx-auto mb-5 shadow-lg shadow-[#0A2540]/20">
                      <StepIcon className="w-7 h-7" />
                    </div>
                    <span className="absolute -top-2 -right-2 md:right-auto md:left-[calc(50%+24px)] text-5xl font-bold text-slate-100 select-none">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1">{step}</h3>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ───── SECTION 6 — DIFFERENTIATION ───── */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fade}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#00C48C]/10 border border-[#00C48C]/30 text-sm text-[#00A67E] mb-6"
            >
              <Zap className="w-4 h-4" />
              IKATone
            </motion.div>
            <motion.h2
              variants={fade}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900"
              data-testid="differentiation-title"
            >
              {t('featuresPage.differentiation')}
            </motion.h2>
          </motion.div>
        </div>
      </section>

      {/* ───── SECTION 7 — SOCIAL PROOF ───── */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            <motion.h2 variants={fade} className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 text-center mb-12">
              {t('featuresPage.socialProof.title')}
            </motion.h2>
            <motion.div variants={stagger} className="grid md:grid-cols-2 gap-8">
              {Array.isArray(reviews) && reviews.map((r, i) => (
                <motion.div
                  key={i}
                  variants={fade}
                  className="bg-slate-50 rounded-2xl p-8 border border-slate-100"
                  data-testid={`review-${i}`}
                >
                  <div className="flex gap-0.5 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 leading-relaxed italic">"{r}"</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ───── SECTION 8 — FINAL CTA ───── */}
      <section className="py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2
              variants={fade}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-10"
              data-testid="features-final-cta-title"
            >
              {t('featuresPage.finalCta.title')}
            </motion.h2>
            <motion.div variants={fade}>
              <Button
                onClick={() => navigate('/products')}
                className="h-14 px-10 text-lg rounded-lg bg-[#00C48C] text-white shadow-lg shadow-[#00C48C]/25 hover:bg-[#00A67E] hover:shadow-[#00C48C]/40 hover:scale-[1.02] transition-all"
                data-testid="features-final-cta-btn"
              >
                {t('featuresPage.finalCta.cta')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
